import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { ResourceEntity, stringifyEntityRef } from '@backstage/catalog-model';
import { ResourceType } from '@internal/backstage-plugin-gcp-common';

interface SearchInputProps {
  searchValue: string;
  onClear: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface Addon {
  appCode: string;
  title: string;
  description?: string;
}

interface LinkAddonDialogProps {
  open: boolean;
  onClose: () => void;
  onLink: (selectedAddons: string[], selectedResources?: string[]) => void;
  currentAddons: string[];
  systemOwner: string;
}

const SearchInput = ({ searchValue, onClear, onChange }: SearchInputProps) => (
  <TextField
    variant="outlined"
    size="small"
    placeholder="Buscar addon"
    fullWidth
    value={searchValue}
    onChange={onChange}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon fontSize="small" />
        </InputAdornment>
      ),
      endAdornment: searchValue && (
        <InputAdornment position="end">
          <IconButton onClick={onClear} size="small">
            <ClearIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
);

export const LinkAddonDialog = ({
                                      open,
                                      onClose,
                                      onLink,
                                      currentAddons,
                                      systemOwner,
                                    }: LinkAddonDialogProps) => {
  const catalogApi = useApi(catalogApiRef);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');

  const { loading: loadingAddons, value: availableAddons = [] } =
    useAsync(async () => {
      if (!open) return [];

      const response = await catalogApi.queryEntities({
        filter: {
          kind: ['System'],
          'metadata.annotations.open.totvs.io/addon-system': 'true',
        },
        fields: [
          'metadata.title',
          'metadata.name',
          'metadata.description',
          'metadata.annotations',
        ],
      });

      return response.items
        .map(item => ({
          appCode: item.metadata?.annotations?.['open.totvs.io/app-code'] || '',
          title: item.metadata?.title || item.metadata?.name || '',
          description: item.metadata?.description,
        }))
        .filter(addon => addon.appCode) as Addon[];
    }, [catalogApi, open]);

  const { loading: loadingResources, value: availableResources = [] } =
    useAsync(async () => {
      if (!open) return [];

      const systemName = systemOwner.replace(/^system:/, '');

      try {
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': ResourceType.GCP_SQL_DATABASE,
            'spec.system': systemName,
          },
          fields: [
            'kind',
            'metadata.title',
            'metadata.name',
            'metadata.namespace',
            'spec.type',
            'spec.system',
          ],
        });

        return [...(response.items || [])].sort((a, b) =>
          (a.metadata?.name || '').localeCompare(b.metadata?.name || ''),
        ) as ResourceEntity[];
      } catch (error) {
        console.error('Erro ao buscar resources:', error);
        return [];
      }
    }, [catalogApi, open, systemOwner]);

  const filteredAddons = useMemo(() => {
    if (!searchValue) return availableAddons;

    const search = searchValue.toLowerCase();
    return availableAddons.filter(addon => {
      const title = addon.title?.toLowerCase() || '';
      const appCode = addon.appCode?.toLowerCase() || '';
      const description = addon.description?.toLowerCase() || '';
      return (
        title.includes(search) ||
        appCode.includes(search) ||
        description.includes(search)
      );
    });
  }, [availableAddons, searchValue]);

  const isInsightsSelected = useMemo(() => {
    return selectedAddons.some(appCode => {
      const addon = availableAddons.find(a => a.appCode === appCode);
      return addon?.title?.toLowerCase().includes('insights');
    });
  }, [selectedAddons, availableAddons]);

  const canLink = useMemo(() => {
    if (selectedAddons.length === 0) return false;
    if (isInsightsSelected && selectedResources.length === 0) return false;
    return true;
  }, [selectedAddons.length, isInsightsSelected, selectedResources.length]);

  const handleToggleAddon = useCallback((appCode: string) => {
    setSelectedAddons(prev =>
      prev.includes(appCode)
        ? prev.filter(code => code !== appCode)
        : [...prev, appCode],
    );
  }, []);

  const handleToggleResource = useCallback((resourceRef: string) => {
    setSelectedResources(prev =>
      prev.includes(resourceRef)
        ? prev.filter(ref => ref !== resourceRef)
        : [...prev, resourceRef],
    );
  }, []);

  const handleLink = useCallback(() => {
    onLink(
      selectedAddons,
      isInsightsSelected ? selectedResources : undefined,
    );
    setSelectedAddons([]);
    setSelectedResources([]);
    setSearchValue('');
    onClose();
  }, [
    selectedAddons,
    selectedResources,
    isInsightsSelected,
    onLink,
    onClose,
  ]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    [],
  );

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedAddons([]);
      setSelectedResources([]);
      setSearchValue('');
    }
  }, [open]);

  const loading = loadingAddons || loadingResources;
  const hasFilteredAddons = filteredAddons.length > 0;
  const hasAvailableResources = availableResources.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Link Addon</Typography>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={2}>
          <SearchInput
            searchValue={searchValue}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
          />
        </Box>

        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress />
          </Box>
        )}

        {!loading && !hasFilteredAddons && (
          <Box mt={2}>
            <Typography align="center">
              {searchValue
                ? 'Nenhum addon encontrado.'
                : 'Nenhum addon disponível.'}
            </Typography>
          </Box>
        )}

        {!loading && hasFilteredAddons && (
          <>
            <List>
              {filteredAddons.map(addon => {
                const isAlreadyLinked = currentAddons.includes(addon.appCode);
                const isSelected = selectedAddons.includes(addon.appCode);

                return (
                  <ListItem
                    key={addon.appCode}
                    button
                    onClick={() =>
                      !isAlreadyLinked && handleToggleAddon(addon.appCode)
                    }
                    disabled={isAlreadyLinked}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isSelected || isAlreadyLinked}
                        disabled={isAlreadyLinked}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1">{addon.title}</Typography>
                          {isAlreadyLinked && (
                            <Chip
                              label="Já vinculado"
                              size="small"
                              color="default"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        addon.description && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="textSecondary"
                          >
                            {addon.description}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                );
              })}
            </List>

            {isInsightsSelected && (
              <Box mt={3}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">
                    <Typography variant="subtitle1" gutterBottom>
                      Select the databases used by Insights
                    </Typography>
                  </FormLabel>
                  {!hasAvailableResources && (
                    <Typography variant="body2" color="textSecondary">
                      No databases available for this system.
                    </Typography>
                  )}

                  {hasAvailableResources && (
                    <FormGroup>
                      {availableResources.map(resource => (
                        <FormControlLabel
                          key={resource.metadata.name}
                          control={
                            <Checkbox
                              checked={selectedResources.includes(
                                stringifyEntityRef(resource),
                              )}
                              onChange={() =>
                                handleToggleResource(stringifyEntityRef(resource))
                              }
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                {resource.metadata.title ||
                                  resource.metadata.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {resource.spec.type}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                </FormControl>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleLink}
          color="primary"
          variant="contained"
          disabled={!canLink}
        >
          Link ({selectedAddons.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkAddonDialog;
